import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
// import * as SecureStore from "expo-secure-store"; // Import SecureStore for token storage

const initialState = {
  user: null,
  MerchantUser: null,
  token: null, // Store user _id as the token
  loading: false,
  error: null,
  points: [],
  isLoading: false,

  loyaltyPointsByBusiness: {}, // Store loyalty points for each business as a dictionary
};

// POST: Create a new user (register)
export const registerUser = createAsyncThunk(
  "user/register",
  async ({ name, phoneNumber }, { rejectWithValue }) => {
    try {
      const response = await axios.post("https://api.aweioio.com/api/users", {
        name,
        phoneNumber,
      });

      const userId = response.data._id;
      await localStorage.setItem("userToken", userId); // Store the user _id securely

      return response.data; // Return user data with _id
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
// AsyncThunk to update points for a business
export const updateBusinessPoints = createAsyncThunk(
  "points/updateBusinessPoints",
  async ({ userId, businessId, points }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        "https://api.aweioio.com/api/users/update-points",
        { userId, businessId, points }
      );
      return response.data; // Return updated user data with points
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// AsyncThunk to get user points for followed businesses
export const getUserPoints = createAsyncThunk(
  "points/getUserPoints",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `https://api.aweioio.com/api/users/points/${userId}`
      );
      return response.data.followedBusinesses; // Return the followed businesses with points
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
// POST: User login
export const loginUser = createAsyncThunk(
  "user/login",
  async ({ phoneNumber }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "https://api.aweioio.com/api/users/login",
        {
          phoneNumber,
        }
      );

      const userId = response.data._id;
      await localStorage.setItem("userToken", userId); // Store the user _id securely

      return response.data; // Return user data with _id
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const followBusiness = createAsyncThunk(
  "user/followBusiness",
  async ({ userId, businessId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `https://api.aweioio.com/api/users/${userId}/follow/${businessId}`
      );
      return response.data; // Return updated user data with the followed business
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
); // AsyncThunk to fetch loyalty points by phone number
export const fetchLoyaltyPointsByBusiness = createAsyncThunk(
  "user/fetchLoyaltyPointsByBusiness",
  async ({ phoneNumber, businessId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `https://api.aweioio.com/api/loyalty-points?phoneNumber=${phoneNumber}`
      );
      return { businessId, loyaltyPoints: response.data.loyaltyPoints }; // Return data along with businessId
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Create the user slice
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.user = null;
      state.token = null;
      localStorage.deleteItemAsync("userToken"); // Remove token from storage on logout
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload; // Save user data in state
        state.token = action.payload._id; // Save the _id as token
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload; // Save user data in state
        state.token = action.payload._id; // Save the _id as token
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(followBusiness.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(followBusiness.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload; // Update user data with followed business
      })
      .addCase(followBusiness.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateBusinessPoints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBusinessPoints.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBusiness = action.payload.followedBusinesses;
        state.points = updatedBusiness; // Update points with the updated business info
      })
      .addCase(updateBusinessPoints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getUserPoints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserPoints.fulfilled, (state, action) => {
        state.loading = false;
        state.points = action.payload; // Store the points for followed businesses
      })
      .addCase(getUserPoints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchLoyaltyPointsByBusiness.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLoyaltyPointsByBusiness.fulfilled, (state, action) => {
        const { businessId, loyaltyPoints } = action.payload;
        state.loading = false;
        state.loyaltyPointsByBusiness[businessId] = loyaltyPoints; // Store points by business ID
      })
      .addCase(fetchLoyaltyPointsByBusiness.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logoutUser } = userSlice.actions;
export default userSlice.reducer;
